import { useEffect, useMemo, useState } from 'react';

import type { ResponsiveTableColumn, TableDensity } from './types';

interface PersistedState {
  hidden: string[];
  density: TableDensity;
}

function loadState(storageKey?: string): PersistedState | null {
  if (!storageKey || typeof window === 'undefined') return null;
  try {
    const raw = localStorage.getItem(`responsiveTable:${storageKey}`);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<PersistedState>;
    return {
      hidden: Array.isArray(parsed.hidden) ? parsed.hidden : [],
      density: parsed.density === 'compact' ? 'compact' : 'comfortable',
    };
  } catch {
    return null;
  }
}

function saveState(storageKey: string, state: PersistedState): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(`responsiveTable:${storageKey}`, JSON.stringify(state));
  } catch {
    // ignore quota errors
  }
}

/**
 * Hook che gestisce visibilità + densità delle colonne nella view desktop.
 * Persistenza opzionale via localStorage per storageKey.
 *
 * Le colonne con `alwaysVisible: true` non possono essere nascoste.
 * `defaultVisible: false` significa nascosta al primo render (utente può
 * mostrarla via menu).
 */
export function useColumnVisibility<T>(
  columns: ResponsiveTableColumn<T>[],
  storageKey?: string,
) {
  const initial = loadState(storageKey);
  const defaultHidden = useMemo(
    () => columns.filter((c) => c.defaultVisible === false).map((c) => c.id),
    [columns],
  );

  const [hidden, setHidden] = useState<Set<string>>(
    () => new Set(initial?.hidden ?? defaultHidden),
  );
  const [density, setDensity] = useState<TableDensity>(initial?.density ?? 'comfortable');

  useEffect(() => {
    if (!storageKey) return;
    saveState(storageKey, { hidden: Array.from(hidden), density });
  }, [hidden, density, storageKey]);

  const toggleColumn = (id: string) => {
    const col = columns.find((c) => c.id === id);
    if (col?.alwaysVisible) return;
    setHidden((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const resetColumns = () => setHidden(new Set(defaultHidden));

  const visibleColumns = useMemo(
    () => columns.filter((c) => c.alwaysVisible || !hidden.has(c.id)),
    [columns, hidden],
  );

  const togglableColumns = useMemo(
    () => columns.filter((c) => !c.alwaysVisible),
    [columns],
  );

  return {
    visibleColumns,
    hidden,
    toggleColumn,
    resetColumns,
    togglableColumns,
    density,
    setDensity,
  };
}
